import React, { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import classNames from 'classnames'
import { find } from 'lodash-es'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import {
  ProviderChrtHighRiskTaskArguments,
  ProviderSelfHarmTask,
  ProviderTask,
  ProviderTaskType,
  RiskSource,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import NavButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/navButton/NavButton'
import RefreshButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/refreshButton/RefreshButton'
import Dashlet from '@lyrahealth-inc/ui-core/lib/molecules/layouts/dashlet/Dashlet'
import { Checkbox } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/Checkbox'
import { useMediaQuerySize } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useMediaQuerySize'

import styles from './selfHarmDashlet.module.scss'
import { selfHarmDashletTabs, selfHarmTasksStates } from '../../../common/constants/appConstants'
import { CLIENT_HOME } from '../../../common/constants/routingConstants'
import { getClientFullName } from '../../../common/utils/utils'
import { useAppDispatch } from '../../../data/store'
import {
  useGetProviderChrtHighRiskTasksQuery,
  useGetProviderSelfHarmTasksQuery,
  usePatchProviderTaskMutation,
} from '../../../providers/dashboard/data/providerTasksApi'
import { setSelectedClientId } from '../../clients/clientDetails/data/ltClientSlice'
import { useAllClientsData } from '../../clients/data/hooks/useAllClientsData'

type SelfHarmDashletProps = {
  providerId: string
}

type RiskTaskWithPatient =
  | (ProviderSelfHarmTask & { patient: ClientListClientObject })
  | (ProviderTask<ProviderChrtHighRiskTaskArguments> & { patient: ClientListClientObject })

const isChrtHighRiskTask = (
  task: RiskTaskWithPatient,
): task is ProviderTask<ProviderChrtHighRiskTaskArguments> & { patient: ClientListClientObject } =>
  task.type === ProviderTaskType.CHRT_HIGH_RISK

// CHRT carries event_date/event_time; self-harm carries self_harm_date/self_harm_time.
const getTaskDateAndTime = (task: RiskTaskWithPatient) =>
  isChrtHighRiskTask(task)
    ? { date: task.arguments?.event_date, time: task.arguments?.event_time }
    : { date: task.arguments?.self_harm_date, time: task.arguments?.self_harm_time }

const getTaskDateString = (task: RiskTaskWithPatient): string | null | undefined => getTaskDateAndTime(task).date

const getTaskSortMs = (task: RiskTaskWithPatient): number => {
  const { date, time } = getTaskDateAndTime(task)
  if (!date || !time) return 0
  return new Date(`${date} ${time}`).getTime()
}

const SelfHarmDashlet: React.FC<SelfHarmDashletProps> = ({ providerId }) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [activeTab, setActiveTab] = useState(selfHarmDashletTabs.NEW)
  const [isRefreshingClients, setIsRefreshingClients] = useState(false)
  const [idToSlide, setIdToSlide] = useState<string | null>(null)
  const [lockedTaskIds, setLockedTaskIds] = useState<string[]>([])
  const { isPreferredNameEnabled } = useFlags()

  const { isMinWidthDesktopL } = useMediaQuerySize()

  const {
    data: selfHarmTasks,
    isLoading: isLoadingTasks,
    refetch: refetchTasks,
  } = useGetProviderSelfHarmTasksQuery({ providerId })

  const {
    data: chrtHighRiskTasks,
    isLoading: isLoadingChrtTasks,
    refetch: refetchChrtTasks,
  } = useGetProviderChrtHighRiskTasksQuery({ providerId })

  const [patchTask] = usePatchProviderTaskMutation()

  const [clients, { isLoading: isLoadingActive, refetch: refetchActive }, { refetch: refetchInactive }] =
    useAllClientsData()

  const allRiskTasksWithClientInfo: RiskTaskWithPatient[] = useMemo(() => {
    const attachClient = <T extends { arguments?: { patient_id?: string | null } | undefined }>(
      task: T,
    ): T & { patient: ClientListClientObject | undefined } => ({
      ...task,
      patient:
        (Array.isArray(clients) && find(clients, (client) => client.id === task?.arguments?.patient_id)) || undefined,
    })

    const isActiveTaskWithPatient = <T extends { status?: string | null; patient: unknown }>(
      task: T,
    ): task is T & { patient: ClientListClientObject } => !!task.patient && task.status !== selfHarmTasksStates.INACTIVE

    return [
      ...(selfHarmTasks?.map(attachClient).filter(isActiveTaskWithPatient) ?? []),
      ...(chrtHighRiskTasks?.map(attachClient).filter(isActiveTaskWithPatient) ?? []),
    ]
  }, [selfHarmTasks, chrtHighRiskTasks, clients])

  const displayedTasks = allRiskTasksWithClientInfo
    .filter((task) =>
      activeTab === selfHarmDashletTabs.NEW ? task.acknowledged_datetime === null : task.acknowledged_datetime !== null,
    )
    .sort((a, b) => getTaskSortMs(b) - getTaskSortMs(a))

  // A row unmounts when its task moves tabs, so release the lock once the task has left the tab it was locked on
  useEffect(() => {
    const currentKeys = new Set(
      allRiskTasksWithClientInfo.map(
        (task) =>
          `${task.acknowledged_datetime === null ? selfHarmDashletTabs.NEW : selfHarmDashletTabs.ADDRESSED}:${task.id}`,
      ),
    )
    setLockedTaskIds((ids) => {
      const next = ids.filter((id) => currentKeys.has(id))
      return next.length === ids.length ? ids : next
    })
  }, [allRiskTasksWithClientInfo])

  const handleCheckboxClick = (task: RiskTaskWithPatient) => {
    const updatedStatusType =
      activeTab === selfHarmDashletTabs.NEW
        ? selfHarmTasksStates.ACKNOWLEDGED
        : selfHarmTasksStates.REQUIRES_ACKNOWLEDGMENT
    setIdToSlide(task.id)
    setLockedTaskIds((ids) => [...ids, `${activeTab}:${task.id}`])
    patchTask({ id: task.id, kind: task.type, data: { status: updatedStatusType } })
      .unwrap()
      .catch(() => setLockedTaskIds((ids) => ids.filter((id) => id !== `${activeTab}:${task.id}`)))
      .finally(() => setIdToSlide((current) => (current === task.id ? null : current)))
  }

  const renderRiskLabels = (task: RiskTaskWithPatient) => {
    if (isChrtHighRiskTask(task)) {
      return (
        <div className={styles.red}>
          <FormattedMessage defaultMessage='CHRT risk' description='Label for CHRT high-risk task in dashlet' />
        </div>
      )
    }
    if (task.arguments?.risk_source === RiskSource.LYRA_AI) {
      return (
        <>
          <div className={styles.red}>Safety risk</div>
          <div className={styles.gray}>Lyra AI</div>
        </>
      )
    }
    return (
      <>
        <div className={styles.red}>ideation</div>
        {isMinWidthDesktopL && <>•</>}
        <div className={task.arguments?.self_harm_plan ? styles.red : styles.gray}>
          {task.arguments?.self_harm_plan ? 'plan' : 'no plan'}
        </div>
        {isMinWidthDesktopL && <>•</>}
        <div className={task.arguments?.self_harm_intent ? styles.red : styles.gray}>
          {task.arguments?.self_harm_intent ? 'intent' : 'no intent'}
        </div>
      </>
    )
  }

  const renderClientListItem = (task: RiskTaskWithPatient) => {
    if (!task || !task.patient) {
      return
    }
    const dateString = getTaskDateString(task)
    const useStackedLabels = !isChrtHighRiskTask(task) && task.arguments?.risk_source === RiskSource.LYRA_AI
    return (
      <li key={task.id} className={`self-harm-row-${task.id}`}>
        <div className={styles['self-harm-item-border']}>
          <div
            className={classNames(
              styles['self-harm-item-wrapper'],
              {
                [styles.slideRight]: idToSlide === task.id && activeTab === selfHarmDashletTabs.NEW,
              },
              {
                [styles.slideLeft]: idToSlide === task.id && activeTab === selfHarmDashletTabs.ADDRESSED,
              },
            )}
          >
            <div className={styles['self-harm-item-checkbox-wrapper']}>
              <Checkbox
                key={task.id}
                name={task.id}
                title=''
                accessibilityLabel={intl.formatMessage({
                  defaultMessage: 'Mark risk alert as addressed',
                  description: 'Accessibility label for the checkbox that acknowledges a risk alert in the dashlet',
                })}
                checked={activeTab === selfHarmDashletTabs.ADDRESSED}
                onPress={() => handleCheckboxClick(task)}
                readOnly={lockedTaskIds.includes(`${activeTab}:${task.id}`)}
              />
            </div>
            <button
              type='button'
              className={styles['self-harm-item']}
              data-test-id={`SelfHarmDashlet-list-item-${task.patient.id}`}
              onClick={() => {
                dispatch(setSelectedClientId(task.patient.id))
                navigate(CLIENT_HOME.route)
              }}
            >
              <div className={styles.left}>
                <div className={styles.name}>{getClientFullName(task.patient, isPreferredNameEnabled)}</div>
                {dateString && (
                  <div className={styles.date}>
                    {intl.formatDate(dateString, {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      timeZone: 'UTC',
                    })}
                  </div>
                )}
              </div>
              <div className={styles.right}>
                <div
                  className={classNames(styles['label-container'], {
                    [styles.stacked]: useStackedLabels,
                  })}
                >
                  {renderRiskLabels(task)}
                </div>
              </div>
            </button>
          </div>
        </div>
      </li>
    )
  }

  const isLoadingClientsOrTasks = isLoadingActive || isLoadingTasks || isLoadingChrtTasks
  const hasAnyRiskTasks = allRiskTasksWithClientInfo.length > 0
  return (
    <div className={styles['self-harm-container']} data-test-id='SelfHarmDashlets-content'>
      <Dashlet
        isLoading={isLoadingClientsOrTasks}
        tabs={
          !isLoadingClientsOrTasks &&
          hasAnyRiskTasks && (
            <>
              <NavButton
                text={selfHarmDashletTabs.NEW}
                isSelected={activeTab === selfHarmDashletTabs.NEW}
                onClick={() => setActiveTab(selfHarmDashletTabs.NEW)}
                styleType='dashlet'
                data-test-id='SelfHarmDashlets-Tab-new'
              />
              <NavButton
                text={selfHarmDashletTabs.ADDRESSED}
                isSelected={activeTab === selfHarmDashletTabs.ADDRESSED}
                onClick={() => setActiveTab(selfHarmDashletTabs.ADDRESSED)}
                styleType='dashlet'
                data-test-id='SelfHarmDashlets-Tab-addressed'
              />
            </>
          )
        }
        title={`Risk alerts${!isLoadingClientsOrTasks ? ` (${displayedTasks.length})` : ''}`}
        buttons={
          <RefreshButton
            data-test-id='SelfHarmDashlets-refresh-button'
            size='20'
            isLoading={isRefreshingClients}
            onClick={() => {
              setIsRefreshingClients(true)
              Promise.all([
                refetchActive().unwrap(),
                refetchInactive().unwrap(),
                refetchTasks().unwrap(),
                refetchChrtTasks().unwrap(),
              ]).then(() => {
                setIsRefreshingClients(false)
              })
            }}
          />
        }
      >
        {!selfHarmTasks && !chrtHighRiskTasks ? (
          ''
        ) : !hasAnyRiskTasks ? (
          <div className={styles['empty-row']}>No clients with risk alerts</div>
        ) : !displayedTasks.length ? (
          <div className={styles['empty-row']}>No {activeTab.toLowerCase()} risk alerts</div>
        ) : (
          displayedTasks.map(renderClientListItem)
        )}
      </Dashlet>
    </div>
  )
}

export default SelfHarmDashlet
